/**
 * 合并单元格滚动吸顶 —— 让 el-table 里 rowspan > 1 的单元格内容在纵向滚动时"钉"在可视区顶部
 *
 * 为什么不能用 position: sticky：
 *   合并格的 td 属于该合并组的首行，组首行一旦滚出滚动容器，sticky 就被父元素边界卡住、
 *   跟着一起消失。所以只能靠 JS 在滚动时动态补偿位移。
 *
 * 【默认行为】全局生效，模板里什么都不用写：
 *   main.js 里 Vue.use(SpanSticky) 后，项目内所有 el-table 的合并单元格自动获得该效果。
 *
 * 【可选】个别表格调参 / 关闭（不写就用全局默认）：
 *   <el-table v-span-sticky="{ bottomGap: 12 }">   只覆盖部分参数，其余继承全局
 *   <el-table v-span-sticky="false">               该表不启用
 *
 *   bottomGap  Number  文字与格底保留的间距(px)，默认 20
 *   threshold  Number  位移超过该值才生效(px)，默认 1；用于挡掉亚像素误差
 *   offsetTop  Number  可视区顶边相对滚动容器的偏移(px)，默认 0（即表头下沿）
 *   selector   String  只处理匹配该选择器的单元格；默认按 rowSpan > 1 自动识别
 *
 * 特性：
 *   - 自动给 rowSpan > 1 的 td 打上标记 class(sticky-cell)，全局样式在 src/assets/style/table.scss，
 *     调用方无需再写 class-name
 *   - 数据刷新 / 排序 / 列显隐导致 tr 重建时，MutationObserver 自动复位、重新标记并重算，无需手动重绑
 *   - 只写 .cell 的 transform，不产生任何样式；滚动 rAF 节流，先读后写避免 layout thrashing
 *   - 表格里没有合并格时 apply 直接短路，普通表格滚动零开销
 *   - resize 用模块级共享监听，实例再多也只挂一个
 */

// 合并格的标记 class（供全局样式挂钩）。只由本模块增删自己加上的那部分，
// 不会误删调用方写在 el-table-column 上的同名 class-name
const MERGED_CLASS = "sticky-cell";

// 支持的配置项及默认值
const DEFAULTS = {
  bottomGap: 20,
  threshold: 1,
  offsetTop: 0,
  selector: "",
};

// Vue.use(SpanSticky, options) 传入的全局配置，作为所有表格的基底
let GLOBAL_OPTIONS = null;

function resolveOptions(value) {
  if (value === false) return null; // 显式关闭
  const opts = Object.assign({}, GLOBAL_OPTIONS || DEFAULTS);
  if (value && typeof value === "object") {
    Object.keys(DEFAULTS).forEach((key) => {
      if (value[key] !== undefined) opts[key] = value[key];
    });
  }
  return opts;
}

function isSameOptions(a, b) {
  if (!a || !b) return false;
  return Object.keys(DEFAULTS).every((key) => a[key] === b[key]);
}

// el-table 根元素本身是 .el-table，逐级兜底拿滚动容器
function resolveWrapper(el) {
  if (!el) return null;
  if (el.classList && el.classList.contains("el-table__body-wrapper")) return el;
  const vm = el.__vue__;
  if (vm && vm.bodyWrapper) return vm.bodyWrapper;
  return el.querySelector(".el-table__body-wrapper");
}

function collectCells(wrapper, opts) {
  const nodes = opts.selector
    ? wrapper.querySelectorAll(opts.selector)
    : wrapper.querySelectorAll("tbody td");
  const list = [];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const td = node.tagName === "TD" ? node : node.closest("td");
    if (!td) continue;
    const cell = td.querySelector(".cell") || td.firstElementChild;
    if (cell) list.push({ td, cell });
  }
  return list;
}

function apply(el) {
  const wrapper = el.__spanStickyWrapper;
  const opts = el.__spanStickyOptions;
  if (!wrapper || !opts) return;
  // 表格里没有合并格：直接短路，普通表格滚动时零开销
  if (!el.__spanStickyHasMerged) return;

  // wrapper 的顶边就是表头下沿，即数据区的可视起点
  const wrapperTop = wrapper.getBoundingClientRect().top + opts.offsetTop;
  const list = collectCells(wrapper, opts);
  const jobs = [];

  for (let i = 0; i < list.length; i++) {
    const { td, cell } = list[i];

    if (td.rowSpan <= 1) {
      // 非合并格不浮动；数据变化后若残留了上一次的位移，顺手清掉
      if (cell.style.transform) jobs.push({ cell, y: 0 });
      continue;
    }

    const tdRect = td.getBoundingClientRect();
    const offset = wrapperTop - tdRect.top; // 该格顶部被滚掉的距离
    let y = 0;
    if (offset > opts.threshold) {
      // 不允许越过本格下边界，否则文字会串进下一组
      const maxY = Math.max(
        tdRect.height - cell.offsetHeight - opts.bottomGap,
        0
      );
      y = Math.min(offset, maxY);
    }
    jobs.push({ cell, y });
  }

  // 先统一读、再统一写，避免读写交错反复触发重排
  for (let j = 0; j < jobs.length; j++) {
    const job = jobs[j];
    job.cell.style.transform = job.y > 0 ? "translateY(" + job.y + "px)" : "";
  }
}

function schedule(el) {
  if (el.__spanStickyRaf) return;
  el.__spanStickyRaf = requestAnimationFrame(() => {
    el.__spanStickyRaf = null;
    apply(el);
  });
}

// 清掉所有由本模块写入的位移
function resetCells(el) {
  if (!el.__spanStickyHasMerged) return; // 从没写过位移，无需清理
  const wrapper = el.__spanStickyWrapper;
  if (!wrapper) return;
  const cells = wrapper.querySelectorAll("tbody td .cell");
  for (let i = 0; i < cells.length; i++) {
    if (cells[i].style.transform) cells[i].style.transform = "";
  }
}

// 给合并格（rowSpan > 1）打上标记 class，供全局样式挂钩；调用方无需再写 class-name
// 只撤销本模块自己加过的那部分，避免误删调用方写在 el-table-column 上的 class-name
function markMergedCells(el) {
  const wrapper = el.__spanStickyWrapper;
  if (!wrapper) return;
  // 先剔除已脱离 DOM 的引用，避免数据反复刷新时数组无限增长
  const prev = el.__spanStickyMarked;
  const marked = prev && prev.length ? prev.filter((td) => td.isConnected) : [];
  el.__spanStickyMarked = marked;
  const tds = wrapper.querySelectorAll("tbody td");
  let hasMerged = false;
  for (let i = 0; i < tds.length; i++) {
    const td = tds[i];
    if (td.rowSpan > 1) {
      hasMerged = true;
      if (!td.classList.contains(MERGED_CLASS)) {
        td.classList.add(MERGED_CLASS);
        marked.push(td);
      }
    } else {
      const at = marked.indexOf(td);
      if (at > -1) {
        td.classList.remove(MERGED_CLASS);
        marked.splice(at, 1);
      }
    }
  }
  el.__spanStickyHasMerged = hasMerged;
}

// 撤销本模块加过的标记 class
function unmarkMergedCells(el) {
  const marked = el.__spanStickyMarked;
  if (!marked || !marked.length) return;
  for (let i = 0; i < marked.length; i++) {
    marked[i].classList.remove(MERGED_CLASS);
  }
  el.__spanStickyMarked = null;
  el.__spanStickyHasMerged = false;
}

// ---------- 模块级共享的 resize 监听：实例再多也只挂一个 ----------
const activeInstances = [];

function sharedResizeHandler() {
  for (let i = 0; i < activeInstances.length; i++) {
    schedule(activeInstances[i]);
  }
}

let resizeBound = false;

function bindSharedResize(el) {
  if (activeInstances.indexOf(el) === -1) activeInstances.push(el);
  if (!resizeBound) {
    window.addEventListener("resize", sharedResizeHandler);
    resizeBound = true;
  }
}

function unbindSharedResize(el) {
  const at = activeInstances.indexOf(el);
  if (at > -1) activeInstances.splice(at, 1);
}

function attach(el, wrapper) {
  if (el.__spanStickyWrapper === wrapper) return;
  if (!el.__spanStickyOptions) return; // 被显式关闭
  el.__spanStickyWrapper = wrapper;

  el.__spanStickyScroll = () => schedule(el);
  wrapper.addEventListener("scroll", el.__spanStickyScroll, { passive: true });

  // 窗口尺寸变化后行高会变，重算一次（共享监听）
  bindSharedResize(el);

  // 数据刷新 / 排序 / 列显隐会重建 tr，自动复位、重新标记并重算，调用方无需手动重绑
  el.__spanStickyObserver = new MutationObserver(() => {
    resetCells(el);
    markMergedCells(el);
    schedule(el);
  });
  el.__spanStickyObserver.observe(wrapper, {
    childList: true,
    subtree: true,
  });

  markMergedCells(el);
  schedule(el);
}

function detach(el) {
  const wrapper = el.__spanStickyWrapper;
  if (wrapper) {
    if (el.__spanStickyScroll) {
      wrapper.removeEventListener("scroll", el.__spanStickyScroll);
    }
    resetCells(el);
    unmarkMergedCells(el);
    el.__spanStickyWrapper = null;
  }
  unbindSharedResize(el);
  if (el.__spanStickyObserver) {
    el.__spanStickyObserver.disconnect();
    el.__spanStickyObserver = null;
  }
  if (el.__spanStickyRaf) {
    cancelAnimationFrame(el.__spanStickyRaf);
    el.__spanStickyRaf = null;
  }
  el.__spanStickyScroll = null;
  el.__spanStickyHasMerged = false;
}

function setup(el, binding) {
  const opts = resolveOptions(binding.value);
  detach(el);
  el.__spanStickyOptions = opts;
  if (!opts) return; // 显式关闭
  bindWrapper(el, 0);
}

// el-table 的 tbody 是异步渲染的，首帧可能还取不到容器，重试若干次
function bindWrapper(el, tries) {
  if (!el.parentNode) return; // 已卸载
  if (!el.__spanStickyOptions) return; // 被显式关闭
  const wrapper = resolveWrapper(el);
  if (wrapper) {
    attach(el, wrapper);
    return;
  }
  if (tries < 30) {
    requestAnimationFrame(() => bindWrapper(el, tries + 1));
  }
}

// 全局兜底挂载：由 mixin 在 ElTable 实例 mounted 时调用，模板无需写指令
function autoAttach(vm) {
  const el = vm.$el;
  if (!el || el.nodeType !== 1) return;
  if (el.__spanStickyWrapper) return; // 已由 v-span-sticky 接管（含自定义参数）
  if (el.__spanStickyOptions === null) return; // 被显式关闭
  vm.$nextTick(() => {
    if (!el.parentNode) return;
    if (el.__spanStickyWrapper) return; // 期间已被指令挂载
    // 注意区分：undefined = 还没初始化过；null = 被指令显式关闭（不能用 ! 判空）
    if (el.__spanStickyOptions === null) return;
    if (el.__spanStickyOptions === undefined) {
      el.__spanStickyOptions = GLOBAL_OPTIONS;
    }
    bindWrapper(el, 0);
  });
}

// 是 element-ui 的表格组件吗（同时兼容根元素就是 .el-table 的二次封装组件）
function isTableVm(vm) {
  if (vm.$options.name === "ElTable") return true;
  const el = vm.$el;
  return !!(
    el &&
    el.nodeType === 1 &&
    el.classList &&
    el.classList.contains("el-table")
  );
}

const directive = {
  bind(el, binding) {
    el.__spanStickyBinding = binding;
  },
  inserted(el, binding) {
    setup(el, binding);
  },
  update(el, binding) {
    const prev = el.__spanStickyOptions;
    const next = resolveOptions(binding.value);
    if (isSameOptions(prev, next)) return;
    el.__spanStickyOptions = next;
    setup(el, binding);
  },
  // 每次 vnode 更新都补一次：覆盖"行数不变、tr 被复用、childList 不触发"的情况
  componentUpdated(el) {
    markMergedCells(el);
    schedule(el);
  },
  unbind(el) {
    detach(el);
    el.__spanStickyBinding = null;
    el.__spanStickyOptions = null;
  },
};

export default {
  /**
   * 全局安装：所有 el-table 自动生效
   * @param {Function} Vue
   * @param {Object} [options] 全局默认参数，同指令参数（bottomGap / threshold / offsetTop / selector）
   */
  install(Vue, options) {
    GLOBAL_OPTIONS = resolveOptions(options);

    Vue.mixin({
      mounted() {
        if (!isTableVm(this)) return;
        autoAttach(this);
      },
      beforeDestroy() {
        if (!isTableVm(this)) return;
        const el = this.$el;
        if (el && el.nodeType === 1) detach(el);
      },
    });

    // 指令保留：个别表格需要单独调参或关闭时仍可写 v-span-sticky
    Vue.directive("span-sticky", directive);
  },
  directive,
};
